<script setup>
import { onMounted, onUnmounted, watch } from 'vue'
import Icon from '@/Components/UI/Icon.vue'

const props = defineProps({
  open: { type: Boolean, required: true },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  variant: { type: String, default: 'default' }, // default|destructive
  closeOnBackdrop: { type: Boolean, default: true },
  size: { type: String, default: 'md' }, // sm|md|lg
})
const emit = defineEmits(['close'])

const sizes = { sm: 'md:max-w-sm', md: 'md:max-w-lg', lg: 'md:max-w-2xl' }

const onKey = (e) => { if (e.key === 'Escape' && props.open) emit('close') }
onMounted(() => window.addEventListener('keydown', onKey))
onUnmounted(() => window.removeEventListener('keydown', onKey))
watch(() => props.open, (v) => { document.body.style.overflow = v ? 'hidden' : '' })
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="open"
        class="fixed inset-0 z-[var(--z-modal)] flex md:items-center md:justify-center md:p-4"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="title ? 'modal-title' : undefined"
        @click.self="closeOnBackdrop && emit('close')"
      >
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" />
        <div
          :class="[
            'relative w-full bg-[color:var(--surface)] shadow-[var(--shadow-lg)] border',
            'h-full md:h-auto md:rounded-[var(--radius-lg)]',
            'flex flex-col',
            'md:max-h-[calc(100vh-2rem)]',
            variant === 'destructive' ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]',
            sizes[size],
          ]"
        >
          <header
            v-if="title"
            class="flex items-start justify-between gap-4 p-5 border-b border-[color:var(--line)]"
          >
            <div>
              <h2
                id="modal-title"
                class="text-base font-medium text-[color:var(--ink)]"
              >
                {{ title }}
              </h2>
              <p
                v-if="description"
                class="mt-1 text-sm text-[color:var(--inkSoft)]"
              >
                {{ description }}
              </p>
            </div>
            <button
              type="button"
              aria-label="Close"
              class="text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors"
              @click="emit('close')"
            >
              <Icon
                name="x"
                :size="20"
              />
            </button>
          </header>
          <div class="p-5 flex-1 overflow-y-auto">
            <slot />
          </div>
          <footer
            v-if="$slots.footer"
            class="flex items-center justify-end gap-2 px-5 pt-0 pb-[max(env(safe-area-inset-bottom),1.25rem)]"
          >
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity var(--dur-slow) var(--ease-standard); }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
